import { useCallback } from 'react'
import { useLazyQuery } from '@apollo/client'
import { setAlertQueue } from 'cache/alerts'
import { setUserLockStatus, setUserProfile } from 'cache/user-profile'
import EMPLOYEE_LOOKUP, {
  IEmployeeLookupRequest,
  IEmployeeLookupResponse,
} from 'queries/employee-lookup'
import { MessageType } from 'ts/enums/messageType.enums'
import errorMessages from '../constants/errorMessages'

const SALES_EMPLOYEE_NUMBER = -555555502

export const useEmployeeLookup = () => {
  const [employeeLookup, { loading, error }] = useLazyQuery<
    IEmployeeLookupResponse,
    IEmployeeLookupRequest
  >(EMPLOYEE_LOOKUP, {
    fetchPolicy: 'no-cache',
  })

  const handleCheckLockByUser = useCallback(
    async (userId: string, _dealNumber: any) => {
      employeeLookup({
        variables: {
          employeeSearchRequest: {
            userId,
            searchKey: '',
            sortBy: 'firstName',
          },
        },
      })
        .then((res) => {
          const employee = res?.data?.employeeList?.employees[0]
          if (employee) {
            const { firstName, lastName } = employee
            setAlertQueue({
              type: MessageType.WARNING,
              message: ` ${errorMessages.DEAL_LOCKED} ${firstName} ${lastName}. Please come back later.`,
            })
            setUserLockStatus(true)
          }
        })
        .catch(() => {
          return setAlertQueue({
            type: MessageType.ERROR,
            message: 'Failed to do an employee lookup',
          })
        })
    },
    [employeeLookup]
  )

  const handleCurrentUserLookupByEmployeeNumber = useCallback(
    async (employeeNumber: number) => {
      const USER_EMAIL = localStorage.getItem('authUserEmailAddress') ?? ''
      employeeLookup({
        variables: {
          employeeSearchRequest: {
            employeeNumbers: [employeeNumber],
            searchKey: '',
          },
        },
      })
        .then((res) => {
          if (res?.data?.employeeList?.employees[0]) {
            const { employees } = res.data.employeeList
            if (employees[0] && USER_EMAIL) {
              setUserProfile({ ...res.data.employeeList.employees[0], emailAddress: USER_EMAIL })
            }
          }
        })
        .catch(() => {
          return setAlertQueue({
            type: MessageType.ERROR,
            message: 'Failed to do an employee lookup',
          })
        })
    },
    [employeeLookup]
  )

  const handleCurrentUserLookup = useCallback(
    async (userEmail: string) => {
      employeeLookup({
        variables: {
          employeeSearchRequest: {
            emailAddresses: [userEmail],
            searchKey: '',
          },
        },
      })
        .then((res) => {
          if (res?.data?.employeeList) {
            const { employees, didError } = res.data.employeeList
            if (employees[0]?.userId && employees[0].userId !== '') {
              setUserProfile(res.data.employeeList.employees[0])
            }

            if (!didError && (!employees[0] || (employees[0] && employees[0].userId === ''))) {
              handleCurrentUserLookupByEmployeeNumber(SALES_EMPLOYEE_NUMBER)
            }
          }
        })
        .catch(() => {
          return setAlertQueue({
            type: MessageType.ERROR,
            message: 'Failed to do an employee lookup',
          })
        })
    },
    [employeeLookup, handleCurrentUserLookupByEmployeeNumber]
  )

  return {
    currentUserLookup: handleCurrentUserLookup,
    checkLockByUser: handleCheckLockByUser,
    isLoading: loading,
    error,
  }
}
